import { Color, Font, Radius, Spacing } from 'zens-storybook'

import type { PreviewRow } from './types'

export const PREVIEW_ROW_LIMIT = 10
export const PREVIEW_COLUMN_LIMIT = 20

export const formatTimestamp = (date?: Date) => {
  if (!date) return ''

  const datePart = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  const timePart = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })

  return `${datePart} ${timePart}`.toLowerCase()
}

export const markdownWrapperStyle = (textColor = Color.premiumBlack, linkColor = Color.darkerBlue) =>
  ({
    color: textColor,
    fontFamily: Font.light,
    fontSize: 16,
    lineHeight: '22px',
    wordBreak: 'break-word' as const,
  })

export const markdownOptions = (linkColor = Color.darkerBlue) => ({
  forceBlock: true,
  overrides: {
    a: {
      props: {
        target: '_blank',
        rel: 'noopener noreferrer',
        style: { color: linkColor, textDecoration: 'underline' },
      },
    },
    strong: { props: { style: { fontFamily: Font.medium } } },
    em: { props: { style: { fontStyle: 'italic' } } },
    h1: { props: { style: { fontFamily: Font.medium, fontSize: 24, margin: `${Spacing.S}px 0` } } },
    h2: { props: { style: { fontFamily: Font.medium, fontSize: 20, margin: `${Spacing.S}px 0` } } },
    h3: { props: { style: { fontFamily: Font.medium, fontSize: 18, margin: `${Spacing.S / 1.5}px 0` } } },
    h4: { props: { style: { fontFamily: Font.medium, fontSize: 16, margin: `${Spacing.S / 1.5}px 0` } } },
    p: { props: { style: { marginTop: 0, marginBottom: Spacing.S / 2 } } },
    ul: { props: { style: { marginBottom: Spacing.S / 2, paddingLeft: Spacing.L } } },
    ol: { props: { style: { marginBottom: Spacing.S / 2, paddingLeft: Spacing.L } } },
    li: { props: { style: { marginTop: Spacing.XS / 2, marginBottom: Spacing.XS / 2 } } },
    pre: {
      props: {
        style: {
          fontFamily: Font.regular,
          backgroundColor: Color.sleepingGrey,
          borderRadius: Radius.S,
          padding: Spacing.S,
          margin: `${Spacing.S}px 0`,
          overflowX: 'auto',
        },
      },
    },
    code: {
      props: {
        style: {
          fontFamily: Font.regular,
          backgroundColor: Color.backgroundGrey,
          borderRadius: Radius.S,
          padding: `${Spacing.XS / 4}px ${Spacing.XS / 2}px`,
        },
      },
    },
  },
})

export const defaultMarkdownOptions = markdownOptions()
export const errorMarkdownOptions = markdownOptions(Color.darkRed)

export const normalizeMarkdown = (text: string) =>
  text.replace(/•\s*/g, '\n- ')

export const flattenMarkdownLinks = (text: string) =>
  text.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')

export const markdownToPlainText = (text: string) =>
  text
    .replace(/\r\n/g, '\n')
    .replace(/```[^\n]*\n([\s\S]*?)```/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[([^\]]*)\]\(([^)]*)\)/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}>\s?/gm, '')
    .replace(/^(\s*)[-*+]\s+/gm, '$1- ')
    .replace(/^(\s*)\d+\.\s+/gm, (_match, indent: string) => `${indent}1. `)
    .replace(/^ {0,3}([-*_]\s*){3,}$/gm, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

export const buildAbsoluteUrl = (baseUrl: string, path: string) => {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path
  }

  if (!baseUrl) {
    return ''
  }

  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  return `${normalizedBase}${normalizedPath}`
}

export const getPreviewCellValue = (row: PreviewRow, column: string, columnIndex: number) => {
  if (Array.isArray(row)) {
    return row[columnIndex]
  }

  if (row && typeof row === 'object') {
    return (row as Record<string, unknown>)[column]
  }

  return ''
}

export const formatPreviewValue = (value: unknown) => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return value.toString()
  return String(value)
}

export const deriveChatTitle = (value: string | null | undefined) => {
  if (!value) {
    return ''
  }
  const normalized = value.trim().replace(/\s+/g, ' ')
  if (!normalized) {
    return ''
  }
  return normalized.slice(0, 30) + '...'
}

export const previewTableStyles = {
  table: {
    borderCollapse: 'collapse' as const,
  },
  inlineTableContainer: {
    maxHeight: 200,
    overflow: 'hidden' as const,
    borderRadius: 4,
  },
  th: {
    backgroundColor: Color.headerGrey,
    padding: Spacing.XS,
    textAlign: 'left' as const,
    border: `0.5px solid ${Color.graphicGrey}`,
    whiteSpace: 'nowrap' as const,
    fontFamily: 'Graphik-Regular',
    fontWeight: 'bold' as const,
    fontSize: 15
  },
  td: {
    padding: Spacing.XS,
    border: `1px solid ${Color.graphicGrey}`,
    backgroundColor: Color.simplyWhite,
    whiteSpace: 'nowrap' as const,
    height: 50,
    fontFamily: Font.light,
    fontSize: 15,
  },
}
